import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, TrendingDown, RefreshCw, FileDown, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API = process.env.REACT_APP_BACKEND_URL;
const n = (v) => Number(v || 0).toLocaleString();
const YEARS = ['2024', '2025', '2026', '2027'];
const TERMS = ['Term 1', 'Term 2', 'Term 3'];

// Child-by-child sponsorship financial register (CASH IN FLOW / CASH OUT FLOW).
// Every figure is derived from Finance income/expense transactions — one source of truth.
export const ChildLedgerTab = ({ onChanged }) => {
  const [data, setData] = useState({ items: [], out_categories: [], totals: {} });
  const [filters, setFilters] = useState({ academic_year: '', term: '', school: '', grade: '', sponsor: '' });
  const [dlg, setDlg] = useState(null); // {type:'donation'|'expense', child}
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && p.append(k, v));
    axios.get(`${API}/api/sponsorship/ledger?${p}`).then((r) => setData(r.data)).catch(() => {});
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const openDlg = (type, child) => {
    setForm({ amount: '', date: new Date().toISOString().slice(0, 10), subcategory: 'school_fees_support',
              sponsor_name: child.sponsor_name || '', reference_number: '', description: '',
              academic_year: filters.academic_year || String(new Date().getFullYear()), term: filters.term || '' });
    setDlg({ type, child });
  };

  const submit = async () => {
    if (!parseFloat(form.amount)) return toast.error('Enter a valid amount');
    try {
      const url = dlg.type === 'donation' ? 'donation' : 'expense';
      await axios.post(`${API}/api/sponsorship/ledger/${url}`, { ...form, child_id: dlg.child.child_id, amount: parseFloat(form.amount), term: form.term || null, academic_year: form.academic_year || null });
      toast.success(`${dlg.type === 'donation' ? 'Donation' : 'Expense'} recorded — Finance ledger updated automatically`);
      setDlg(null); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const exportLedger = async (format) => {
    const p = new URLSearchParams({ format });
    Object.entries(filters).forEach(([k, v]) => v && p.append(k, v));
    try {
      const r = await axios.get(`${API}/api/sponsorship/ledger/export?${p}`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = `child_ledger.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      toast.success(`${format === 'pdf' ? 'PDF' : 'Excel'} ledger downloaded`);
    } catch { toast.error('Export failed'); }
  };

  const openProfile = async (row) => {
    try {
      const r = await axios.get(`${API}/api/sponsorship/ledger/child/${row.child_id}`);
      setProfile(r.data);
    } catch { toast.error('Failed to load child profile'); }
  };

  const th = 'px-2 py-1.5 text-left text-[10px] font-semibold uppercase border-b border-r whitespace-nowrap';
  const td = 'px-2 py-1.5 border-b border-r text-xs tabular-nums text-right';
  const cats = data.out_categories;

  return (
    <div className="space-y-3" data-testid="child-ledger-tab">
      <div className="flex flex-wrap items-end gap-2">
        <Select value={filters.academic_year || 'all'} onValueChange={(v) => setFilters({ ...filters, academic_year: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-32" data-testid="ledger-year"><SelectValue placeholder="Year" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Years</SelectItem>{YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.term || 'all'} onValueChange={(v) => setFilters({ ...filters, term: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-28" data-testid="ledger-term"><SelectValue placeholder="Term" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Terms</SelectItem>{TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Input className="w-32" placeholder="Class (e.g. P.5)" value={filters.grade} onChange={(e) => setFilters({ ...filters, grade: e.target.value })} data-testid="ledger-class" />
        <Input className="w-36" placeholder="Sponsor" value={filters.sponsor} onChange={(e) => setFilters({ ...filters, sponsor: e.target.value })} data-testid="ledger-sponsor" />
        <Button variant="outline" size="sm" onClick={load} data-testid="ledger-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
        <Button variant="outline" size="sm" onClick={() => exportLedger('excel')} data-testid="ledger-export-excel"><FileDown className="h-3.5 w-3.5 mr-1" /> Excel</Button>
        <Button variant="outline" size="sm" onClick={() => exportLedger('pdf')} data-testid="ledger-export-pdf"><FileText className="h-3.5 w-3.5 mr-1" /> PDF</Button>
        <div className="ml-auto flex gap-4 text-xs">
          <span>Received: <b className="text-green-700">UGX {n(data.totals.donation)}</b></span>
          <span>Spent: <b className="text-red-600">UGX {n(data.totals.total_outflow)}</b></span>
          <span>Balance: <b>UGX {n(data.totals.balance)}</b></span>
          <span>Children: <b>{data.totals.children || 0}</b></span>
        </div>
      </div>

      <Card><CardContent className="p-0 overflow-auto">
        <table className="w-full min-w-[1200px] border-collapse" data-testid="ledger-table">
          <thead>
            <tr className="bg-primary/10">
              <th className={th} rowSpan={2}>NO.</th>
              <th className={`${th} text-left`} rowSpan={2}>NAMES</th>
              <th className={th} rowSpan={2}>CLASS</th>
              <th className={`${th} text-center bg-green-100 dark:bg-green-950/40`} colSpan={3}>CASH IN FLOW</th>
              <th className={`${th} text-center bg-red-100 dark:bg-red-950/40`} colSpan={cats.length + 2}>CASH OUT FLOW</th>
              <th className={th} rowSpan={2}></th>
            </tr>
            <tr className="bg-muted">
              <th className={th}>Opening Balance</th><th className={th}>Donation</th><th className={th}>Total Amount</th>
              {cats.map((c) => <th key={c.key} className={th}>{c.label}</th>)}
              <th className={th}>Total Out</th><th className={th}>Balance</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((r, i) => (
              <tr key={r.child_id} className="hover:bg-muted/40" data-testid={`ledger-row-${r.child_id}`}>
                <td className={td}>{i + 1}</td>
                <td className={`${td} text-left`}>
                  <button className="underline decoration-dotted font-medium text-primary" onClick={() => openProfile(r)} data-testid={`ledger-name-${r.child_id}`}>{r.name}</button>
                </td>
                <td className={`${td} text-center`}>{r.grade || '—'}</td>
                <td className={td}>{n(r.opening_balance)}</td>
                <td className={`${td} text-green-700 font-medium`}>{n(r.donation)}</td>
                <td className={`${td} font-semibold`}>{n(r.total_amount)}</td>
                {cats.map((c) => <td key={c.key} className={td}>{r.outflows[c.key] ? n(r.outflows[c.key]) : '—'}</td>)}
                <td className={`${td} text-red-600 font-medium`}>{n(r.total_outflow)}</td>
                <td className={`${td} font-semibold ${r.balance < 0 ? 'text-red-600' : ''}`}>{n(r.balance)}</td>
                <td className={`${td} whitespace-nowrap`}>
                  <Button variant="ghost" size="sm" className="h-6 px-1.5 text-green-700" title="Record donation" onClick={() => openDlg('donation', r)} data-testid={`add-donation-${r.child_id}`}><Plus className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="sm" className="h-6 px-1.5 text-red-600" title="Record expense" onClick={() => openDlg('expense', r)} data-testid={`add-expense-${r.child_id}`}><TrendingDown className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={cats.length + 9} className="text-center py-8 text-muted-foreground text-sm">No sponsored children match the filters</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
      <p className="text-[11px] text-muted-foreground">Total Amount = Opening Balance + Donation · Balance = Total Amount − Total Cash Outflow (calculated automatically; totals cannot be typed over). All entries post directly into Finance → Income/Expenses.</p>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md" data-testid="ledger-entry-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{dlg?.type === 'donation' ? 'Record Donation' : 'Record Expense'} — {dlg?.child?.name}</DialogTitle>
            <DialogDescription>Posts directly to Finance ({dlg?.type === 'donation' ? 'Income' : 'Expenses'}) tagged to this child. No double entry.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {dlg?.type === 'expense' && (
              <div className="col-span-2 space-y-1"><Label className="text-xs">Category</Label>
                <Select value={form.subcategory} onValueChange={(v) => setForm({ ...form, subcategory: v })}>
                  <SelectTrigger data-testid="ledger-expense-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                </Select></div>
            )}
            {dlg?.type === 'donation' && (
              <div className="col-span-2 space-y-1"><Label className="text-xs">Sponsor</Label>
                <Input value={form.sponsor_name} onChange={(e) => setForm({ ...form, sponsor_name: e.target.value })} data-testid="ledger-sponsor-input" /></div>
            )}
            <div className="space-y-1"><Label className="text-xs">Amount (UGX)</Label>
              <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="ledger-amount-input" /></div>
            <div className="space-y-1"><Label className="text-xs">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="ledger-date-input" /></div>
            <div className="space-y-1"><Label className="text-xs">Academic Year</Label>
              <Input value={form.academic_year} onChange={(e) => setForm({ ...form, academic_year: e.target.value })} data-testid="ledger-ay-input" /></div>
            <div className="space-y-1"><Label className="text-xs">Term</Label>
              <Select value={form.term || 'none'} onValueChange={(v) => setForm({ ...form, term: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="ledger-term-input"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="col-span-2 space-y-1"><Label className="text-xs">Reference / Receipt No. (optional)</Label>
              <Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} data-testid="ledger-ref-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={submit} data-testid="ledger-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!profile} onOpenChange={(o) => !o && setProfile(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ledger-child-profile">
          {profile && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">{profile.child.name} — Sponsorship Financial History</DialogTitle>
                <DialogDescription>
                  Class {profile.child.grade || '—'} · {profile.child.school || '—'} · Sponsor: {profile.child.sponsor_name || '—'}
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-3 gap-2 text-center">
                {[['Total Donations', profile.total_donations, 'text-green-700'], ['Total Spent', profile.total_spent, 'text-red-600'], ['Current Balance', profile.balance, '']].map(([l, v, c]) => (
                  <div key={l} className="border rounded-md p-2"><p className="text-[10px] text-muted-foreground">{l}</p><p className={`font-heading font-bold tabular-nums ${c}`}>UGX {n(v)}</p></div>
                ))}
              </div>
              {[['Donations (Cash In)', profile.donations, 'source'], ['Spending History (Cash Out)', profile.expenses, 'subcategory']].map(([title, list, catKey]) => (
                <div key={title}>
                  <p className="text-xs font-semibold mb-1">{title}</p>
                  <table className="w-full text-xs">
                    <thead><tr>{['Date', 'Category', 'Description', 'Year/Term', 'Amount'].map((h) => <th key={h} className="text-left px-1.5 py-1 text-[10px] uppercase text-muted-foreground border-b">{h}</th>)}</tr></thead>
                    <tbody>
                      {list.map((t) => (
                        <tr key={t.id} className="border-b">
                          <td className="px-1.5 py-1">{t.date}</td>
                          <td className="px-1.5 py-1 capitalize">{(t[catKey] || t.category || '—').replace(/_/g, ' ')}</td>
                          <td className="px-1.5 py-1">{t.description || '—'}</td>
                          <td className="px-1.5 py-1">{[t.academic_year, t.term].filter(Boolean).join(' ') || '—'}</td>
                          <td className="px-1.5 py-1 text-right tabular-nums">{n(t.amount)}</td>
                        </tr>
                      ))}
                      {list.length === 0 && <tr><td colSpan={5} className="text-center py-3 text-muted-foreground">None recorded</td></tr>}
                    </tbody>
                  </table>
                </div>
              ))}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChildLedgerTab;
